import { type HTMLAttributes, type ReactNode, useId } from "react";

export interface ScoreRingProps {
  /** Current value, e.g. a CRS score or a CLB level. */
  value: number;
  max: number;
  label: string;
  /** Visible caption under the value, e.g. « estimation ». */
  caption?: string;
  size?: number;
}

/** Animated ring for scores (SCG, NCLC). Always paired with a caption such as « estimation ». */
export function ScoreRing({ value, max, label, caption, size = 140 }: ScoreRingProps) {
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.max(0, Math.min(1, max > 0 ? value / max : 0));
  return (
    <div
      className="ao-ring"
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} aria-hidden="true">
        <circle
          className="ao-ring__track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="ao-ring__value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
        />
      </svg>
      <span className="ao-ring__label">
        {value}
        {caption ? <span className="ao-ring__caption">{caption}</span> : null}
      </span>
    </div>
  );
}

/** default = neutral grey; accent, success, warning, danger, info = tinted; offer = amber fill. */
export type BadgeTone = "default" | "accent" | "success" | "warning" | "danger" | "info" | "offer";

export function Badge({
  tone = "default",
  dot = false,
  className = "",
  children,
}: {
  tone?: BadgeTone;
  /** Small coloured dot before the text (statuses). */
  dot?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const classes = ["ao-badge", tone !== "default" && `ao-badge--${tone}`, dot && "ao-badge--dot", className]
    .filter(Boolean)
    .join(" ");
  return <span className={classes}>{children}</span>;
}

export function Skeleton({ style, className = "", ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`ao-skeleton ${className}`.trim()}
      aria-hidden="true"
      style={{ height: 16, ...style }}
      {...rest}
    />
  );
}

/** A few lines of text placeholder; the last one is shorter. */
export function SkeletonText({ lines = 3, label }: { lines?: number; label?: string }) {
  return (
    <div className="ao-skeleton-stack" role={label ? "status" : undefined} aria-label={label}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} style={{ height: 12, width: i === lines - 1 ? "60%" : "100%" }} />
      ))}
    </div>
  );
}

export function Spinner({ label, size = 16 }: { label?: string; size?: number }) {
  return (
    <span
      className="ao-spinner"
      role={label ? "status" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ width: size, height: size }}
    />
  );
}

export interface EmptyStateProps {
  /** A lucide icon (size 22, strokeWidth 1.75), decorative. */
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Buttons (one primary at most). */
  actions?: ReactNode;
  compact?: boolean;
  className?: string;
}

/** What to show when a list is empty: an icon, one sentence, the next action. */
export function EmptyState({ icon, title, description, actions, compact = false, className = "" }: EmptyStateProps) {
  return (
    <div className={["ao-empty", compact && "ao-empty--compact", className].filter(Boolean).join(" ")}>
      {icon ? (
        <span className="ao-empty__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <p className="ao-empty__title">{title}</p>
      {description ? <p className="ao-empty__description">{description}</p> : null}
      {actions ? <div className="ao-empty__actions">{actions}</div> : null}
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Small line above the title (section, breadcrumb). */
  eyebrow?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

/** Large title of a screen (h1), its subtitle and its actions (primary action last). */
export function PageHeader({ title, subtitle, eyebrow, actions, className = "" }: PageHeaderProps) {
  return (
    <header className={`ao-page-header ${className}`.trim()}>
      <div className="ao-page-header__text">
        {eyebrow ? <p className="ao-page-header__eyebrow">{eyebrow}</p> : null}
        <h1 className="ao-page-header__title">{title}</h1>
        {subtitle ? <p className="ao-page-header__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="ao-page-header__actions">{actions}</div> : null}
    </header>
  );
}

/** Round initials (people). Decorative: the name must also be written next to it. */
export function Avatar({ name, size = "md" }: { name: string; size?: "md" | "lg" }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <span className={size === "lg" ? "ao-avatar ao-avatar--lg" : "ao-avatar"} aria-hidden="true">
      {initials || "?"}
    </span>
  );
}

/** Keyboard key, e.g. <Kbd>⌘</Kbd><Kbd>K</Kbd>. */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="ao-kbd">{children}</kbd>;
}

/** Shown when the network drops: entries are kept and sent later (N12). */
export function OfflineBanner({ children }: { children: ReactNode }) {
  return (
    <div className="ao-offline" role="status" aria-live="polite">
      {children}
    </div>
  );
}

/** Tone of a figure: its icon tile, its sparkline and its accent colour. */
export type StatTone = "accent" | "success" | "warning" | "danger" | "info" | "neutral";

/**
 * Tiny line chart of a series (e.g. new prospects per day). Decorative when `label` is omitted;
 * drawn with currentColor only (the tone of the parent), area under the line in a soft fade.
 */
export function Sparkline({ values, label, className = "" }: { values: readonly number[]; label?: string; className?: string }) {
  const id = useId().replace(/:/g, "");
  if (values.length < 2) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const line = values
    .map((v, i) => `${((i / (values.length - 1)) * 100).toFixed(2)},${(30 - ((v - min) / span) * 26).toFixed(2)}`)
    .join(" ");
  const area = `M0,32 L${line.replace(/ /g, " L")} L100,32 Z`;
  return (
    <svg
      className={`ao-sparkline ${className}`.trim()}
      viewBox="0 0 100 32"
      preserveAspectRatio="none"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      <defs>
        <linearGradient id={`${id}-fade`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id}-fade)`} />
      <polyline points={line} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export interface StatTrend {
  /** Already formatted, e.g. « +3 » or « +12 % ». */
  value: ReactNode;
  direction: "up" | "down" | "flat";
  /** Whether going up is good news (default true): up is green, down red; false flips it (e.g. overdue). */
  positive?: boolean;
  /** Visible context, e.g. « vs semaine dernière ». */
  label?: ReactNode;
}

export interface StatCardProps {
  /** A lucide icon (size 18, strokeWidth 1.75), decorative. */
  icon?: ReactNode;
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: StatTone;
  trend?: StatTrend;
  /** Optional series for a sparkline (only when the data really exists). */
  series?: readonly number[];
  seriesLabel?: string;
  /** Wraps the content, e.g. (inner) => <Link to="/leads" className="ao-stat__link">{inner}</Link>. */
  wrap?: (inner: ReactNode) => ReactNode;
  className?: string;
  as?: "article" | "div" | "section";
}

const TREND_PATH = { up: "M3 10 L8 5 L13 10", down: "M3 6 L8 11 L13 6", flat: "M3 8 L13 8" } as const;

/** Key figure: icon in a tinted tile, large tabular value, optional trend and sparkline. */
export function StatCard({
  icon,
  label,
  value,
  hint,
  tone = "accent",
  trend,
  series,
  seriesLabel,
  wrap,
  className = "",
  as: Tag = "article",
}: StatCardProps) {
  const good = trend ? (trend.direction === "flat" ? undefined : (trend.direction === "up") === (trend.positive ?? true)) : undefined;
  const inner = (
    <>
      <span className="ao-stat__head">
        {icon ? (
          <span className="ao-stat__icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className="ao-stat__label">{label}</span>
      </span>
      <span className="ao-stat__body">
        <span className="ao-stat__value">{value}</span>
        {series ? <Sparkline values={series} label={seriesLabel} /> : null}
      </span>
      {trend || hint ? (
        <span className="ao-stat__foot">
          {trend ? (
            <span className={`ao-stat__trend ao-stat__trend--${good === undefined ? "flat" : good ? "good" : "bad"}`}>
              <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
                <path d={TREND_PATH[trend.direction]} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {trend.value}
            </span>
          ) : null}
          {trend?.label ? <span className="ao-stat__hint">{trend.label}</span> : null}
          {hint && !trend?.label ? <span className="ao-stat__hint">{hint}</span> : null}
        </span>
      ) : null}
    </>
  );
  return (
    <Tag className={`ao-glass ao-card ao-stat ao-stat--${tone} ${className}`.trim()}>
      {wrap ? wrap(inner) : <span className="ao-stat__link">{inner}</span>}
    </Tag>
  );
}

/** Title of a group of cards on a page (h2), with an optional action on the right. */
export function SectionHeader({
  title,
  subtitle,
  actions,
  id,
  className = "",
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  id?: string;
  className?: string;
}) {
  return (
    <div className={`ao-section-header ${className}`.trim()}>
      <div className="ao-section-header__text">
        <h2 className="ao-section-header__title" id={id}>
          {title}
        </h2>
        {subtitle ? <p className="ao-section-header__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="ao-section-header__actions">{actions}</div> : null}
    </div>
  );
}
